import { DEFAULT_SORT_BY, FiltersState, initialFiltersState } from '../constants';
import {
  getRewardTypeKeyFromRewardType,
  getStatusKeyFromEventStatus,
  validateBoolean,
  validateEventType,
  validateRewardTypes,
  validateSearch,
  validateSortBy,
  validateStatuses,
} from './validation';

export const getSelectedFiltersCount = (filters: FiltersState): number => {
  let count = 0;

  if (filters.sortBy !== initialFiltersState.sortBy) {
    count++;
  }

  const statusChanged = JSON.stringify(filters.status) !== JSON.stringify(initialFiltersState.status);
  if (statusChanged) {
    count++;
  }

  if (filters.special.VIP_ONLY !== initialFiltersState.special.VIP_ONLY) {
    count++;
  }

  const hasRewardTypeSelected = Object.entries(filters.rewardType).some(([key, selected]) => key !== 'ALL' && selected);
  if (hasRewardTypeSelected) {
    count++;
  }

  return count;
};

export const areFiltersEqual = (filters1: FiltersState, filters2: FiltersState): boolean => {
  const statusEqual = JSON.stringify(filters1.status) === JSON.stringify(filters2.status);
  const rewardTypeEqual = JSON.stringify(filters1.rewardType) === JSON.stringify(filters2.rewardType);

  return (
    filters1.sortBy === filters2.sortBy &&
    statusEqual &&
    filters1.eventType === filters2.eventType &&
    filters1.search === filters2.search &&
    filters1.special.VIP_ONLY === filters2.special.VIP_ONLY &&
    rewardTypeEqual
  );
};

export const filtersToSearchParams = (filters: FiltersState): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.sortBy && filters.sortBy !== DEFAULT_SORT_BY) {
    params.set('sortBy', filters.sortBy);
  }

  const selectedStatuses = Object.entries(filters.status)
    .filter(([key, selected]) => key !== 'ALL' && selected)
    .map(([key]) => key);
  selectedStatuses.forEach((status) => {
    params.append('status', status);
  });

  if (filters.eventType && filters.eventType !== 'all') {
    params.set('eventType', filters.eventType);
  }
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (filters.special.VIP_ONLY) {
    params.set('vipOnly', 'true');
  }

  const selectedRewardTypes = Object.entries(filters.rewardType)
    .filter(([key, selected]) => key !== 'ALL' && selected)
    .map(([key]) => key);
  selectedRewardTypes.forEach((rewardType) => {
    params.append('rewardType', rewardType);
  });

  return params;
};

export const searchParamsToFilters = (searchParams: URLSearchParams, initialFilters: FiltersState): FiltersState => {
  const filters: FiltersState = {
    ...initialFilters,
    status: { ...initialFilters.status },
    special: { ...initialFilters.special },
    rewardType: { ...initialFilters.rewardType },
  };

  const sortBy = validateSortBy(searchParams.get('sortBy'));
  if (sortBy) {
    filters.sortBy = sortBy;
  }

  const statusArray = searchParams.getAll('status');
  const validatedStatuses = validateStatuses(statusArray);
  if (validatedStatuses.length > 0) {
    Object.keys(filters.status).forEach((key) => (filters.status[key] = false));
    validatedStatuses.forEach((status) => {
      const statusKey = getStatusKeyFromEventStatus(status);
      if (statusKey) {
        filters.status[statusKey] = true;
      }
    });
  }

  const eventType = validateEventType(searchParams.get('eventType'));
  if (eventType) {
    filters.eventType = eventType;
  }

  const search = validateSearch(searchParams.get('search'));
  if (search) {
    filters.search = search;
  }

  const vipOnly = validateBoolean(searchParams.get('vipOnly'));
  if (vipOnly !== null) {
    filters.special.VIP_ONLY = vipOnly;
  }

  const rewardTypeArray = searchParams.getAll('rewardType');
  const validatedRewardTypes = validateRewardTypes(rewardTypeArray);
  if (validatedRewardTypes.length > 0) {
    Object.keys(filters.rewardType).forEach((key) => (filters.rewardType[key] = false));
    validatedRewardTypes.forEach((rt) => {
      const key = getRewardTypeKeyFromRewardType(rt);
      if (key) {
        filters.rewardType[key] = true;
      }
    });
  } else {
    Object.keys(filters.rewardType).forEach((key) => (filters.rewardType[key] = false));
    filters.rewardType.ALL = true;
  }

  return filters;
};
